import { chieuCaoCanNangApi } from '../../../api/chieuCaoCanNangApi';
import { CheckIcon, DownloadIcon, EyeIcon, PencilIcon, UploadIcon, XIcon } from '@primer/octicons-react';
import { ActionList, ActionMenu, Box, Button, IconButton, SegmentedControl, TextInput, Tooltip } from '@primer/react';
import DataTable from '../../../components/ui/data-table/DataTable';
import Modal from '../../../components/ui/modal';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { tinhTuoi, formatThangTuoi } from '../../../utils/tinhThangTuoiHelper';
import { useDispatch, useSelector } from 'react-redux';
import LopHanhChinhTreeView from '../../../components/lop-tree-view/LopHanhChinhTreeView';
import HocSinhShortInfo from '../../../components/hoc-sinh/HocSinhShortInfo';
import { PlaceHolder } from '../../../components/place-holder';
import { useCommonContext } from '../../../contexts/common';
import { useCommonSelectedHook } from '../../../hooks/useCommonSelectedHook';
import { useMonths } from '../../../hooks/useMonths';
import { ePageBaseStatus } from '../../../models/ePageBaseStatus';
import { ILopSelectedItemRequest } from '../../../models/request/danh-muc/ILopSelectedItemRequest';
import { actions } from '../../../state/actions/actionsWrapper';
import { RootState } from '../../../state/reducers';
import BMIChartChart from './chart/BMIChart';
import CanNangChart from './chart/CanNangChart';
import ChieuCaoChart from './chart/ChieuCaoChart';
import ChieuCaoCanNangImport from './chieu-cao-can-nang-import/ChieuCaoCanNangImport';

interface ILopSelected {
    dm_lop: ILopSelectedItemRequest;
    ten_lop: string;
}

export const getGradeFromName = (name: string): number => {
    if (!name) return 6;
    const match = name.match(/\d+/);
    if (match) {
        const grade = parseInt(match[0], 10);
        if (grade >= 1 && grade <= 12) return grade;
    }
    return 6; // fallback
};

// BMI label helper
export const getBMILabel = (bmi?: number, grade: number = 6): { label: string; color: string } => {
    if (bmi == null) return { label: '-', color: 'fg.muted' };
    
    let thieu_can = 0, tot_max = 0, ng_thua_can_max = 0;
    switch(grade) {
        case 1: thieu_can = 13.4; tot_max = 17.2; ng_thua_can_max = 18.9; break;
        case 2: thieu_can = 13.4; tot_max = 17.8; ng_thua_can_max = 19.8; break;
        case 3: thieu_can = 13.5; tot_max = 18.4; ng_thua_can_max = 20.8; break;
        case 4: thieu_can = 13.7; tot_max = 19.2; ng_thua_can_max = 21.9; break;
        case 5: thieu_can = 14; tot_max = 20.1; ng_thua_can_max = 23; break;
        case 6: thieu_can = 14.3; tot_max = 21; ng_thua_can_max = 24.2; break;
        case 7: thieu_can = 14.7; tot_max = 21.8; ng_thua_can_max = 25.2; break;
        case 8: thieu_can = 15.2; tot_max = 22.8; ng_thua_can_max = 26.2; break;
        case 9: thieu_can = 15.8; tot_max = 23.2; ng_thua_can_max = 27.2; break;
        case 10: thieu_can = 16.2; tot_max = 24; ng_thua_can_max = 28.1; break;
        case 11: thieu_can = 16.8; tot_max = 24.8; ng_thua_can_max = 29; break;
        case 12: thieu_can = 17.1; tot_max = 25.2; ng_thua_can_max = 29.8; break;
        default: thieu_can = 14.3; tot_max = 21; ng_thua_can_max = 24.2; break;
    }

    if (bmi < thieu_can) return { label: 'Thiếu cân', color: '#cf222e' };
    if (bmi <= tot_max) return { label: 'SKDD Tốt', color: '#1a7f37' };
    if (bmi <= ng_thua_can_max) return { label: 'NC Thừa cân', color: '#bf8700' };
    return { label: 'Thừa cân', color: '#cf222e' };
};

const ChieuCaoCanNangPage = () => {
    const { translate } = useCommonContext();
    const dispatch = useDispatch();
    const { thangs } = useMonths();
    const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
    const { dm_truong_selected_id, dm_he_selected_id } = useSelector((x: RootState) => x.common);

    // Lop treeview state
    const { status: lopStatus, treeViewItems } = useSelector((x: RootState) => x.danhMucWrapper.lop);
    const [lopSelected, setLopSelected] = useState<ILopSelected>({
        dm_lop: {} as ILopSelectedItemRequest,
        ten_lop: '',
    });

    // ChieuCaoCanNang state
    const chieuCaoCanNangState = useSelector((state: any) => state.chieuCaoCanNang);
    const { status, ketQuas, hocSinhs, filter } = chieuCaoCanNangState ?? {
        status: 'is_not_initialization',
        ketQuas: [],
        hocSinhs: [],
        filter: { dm_lop_id: 0, nam_hoc: '', thang: new Date().getMonth() + 1 }
    };

    const [ngay_do, setNgayDo] = useState<Date>(new Date());
    const [showImportModal, setShowImportModal] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isEdit, setIsEdit] = useState(false);
    const [editedRows, setEditedRows] = useState<Record<number, { chieu_cao?: string; can_nang?: string; ngay_do?: string }>>({});
    const [hocSinhChartData, setHocSinhChartData] = useState<any[]>([]);
    const [showChartModal, setShowChartModal] = useState<boolean>(false);
    const [selectedHocSinh, setSelectedHocSinh] = useState<any>(null);
    const [chartTabIndex, setChartTabIndex] = useState(0);

    const ketQuasThangHienTai = useMemo(() => {
        return ketQuas.filter((x: any) => x.thang === filter.thang);
    }, [filter.thang, ketQuas]);

    // Merge hocSinhs with ketQuas (same pattern as source project)
    const dataSource = useMemo(() => {
        if (!lopSelected.ten_lop) return [];
        return hocSinhs.map((hs: any) => {
            const ketQua = ketQuasThangHienTai.find((x: any) => x.ts_hocsinh_id === hs.id);
            return {
                ...hs,
                ts_hocsinh_id: hs.id,
                chieu_cao: ketQua?.chieu_cao,
                can_nang: ketQua?.can_nang,
                bmi: ketQua?.bmi,
                ngay_do: ketQua?.ngay_do,
                id: ketQua?.id ?? 0,
            };
        });
    }, [ketQuasThangHienTai, hocSinhs, lopSelected.ten_lop]);

    // Load lop tree view
    const handleReloadLops = useCallback(() => {
        dispatch(
            actions.danhMucWrapper.lop.loadTreeCteStart({
                nam_hoc: nam_hoc,
                dm_truong_id: dm_truong_selected_id,
                dm_he_id: dm_he_selected_id,
                dm_khoi_id: 0,
                dm_coso_id: dm_coso_id
            })
        );
    }, [nam_hoc, dm_coso_id, dm_truong_selected_id, dm_he_selected_id]);

    useEffect(() => {
        handleReloadLops();
    }, [nam_hoc, dm_coso_id]);

    useEffect(() => {
        if (lopStatus == ePageBaseStatus.is_need_reload) {
            handleReloadLops();
            setLopSelected({ dm_lop: {} as ILopSelectedItemRequest, ten_lop: '' });
        }
    }, [lopStatus]);

    // Update filter when nam_hoc changes
    useEffect(() => {
        if (nam_hoc !== filter.nam_hoc) {
            dispatch(actions.chieuCaoCanNang.CHANGE_FILTER({
                ...filter,
                nam_hoc: nam_hoc
            }));
        }
    }, [nam_hoc, filter.nam_hoc]);

    // Load students + measurement data when lop changes
    useEffect(() => {
        if (filter.dm_lop_id > 0 && filter.nam_hoc) {
            // Step 1: Load all students in the class (same API as LopHanhChinhPage)
            dispatch(actions.chieuCaoCanNang.LOAD_HOC_SINH_START({
                dm_lop_id: filter.dm_lop_id,
                dm_truong_id: dm_truong_selected_id,
                dm_khoi_id: 0,
                dm_he_id: 0,
                nam_hoc: filter.nam_hoc,
            }));
            // Step 2: Load all measurement data (thang: 0 = all months)
            dispatch(actions.chieuCaoCanNang.LOAD_KETQUA_START({
                dm_lop_id: filter.dm_lop_id,
                nam_hoc: filter.nam_hoc,
                thang: 0
            }));
        }
    }, [filter.dm_lop_id, filter.nam_hoc]);

    const handleLopSelected = (data: ILopSelectedItemRequest, ten_lop?: string) => {
        setLopSelected({
            dm_lop: data,
            ten_lop: ten_lop ?? '',
        });
        dispatch(actions.chieuCaoCanNang.CHANGE_FILTER({
            ...filter,
            dm_lop_id: data.dm_lop_id,
            nam_hoc: nam_hoc
        }));
    };

    const handleFieldChange = useCallback((hocSinhId: number, field: string, value: string) => {
        setEditedRows(prev => ({
            ...prev,
            [hocSinhId]: {
                ...prev[hocSinhId],
                [field]: value
            }
        }));
    }, []);

    const handleSaveAll = useCallback(() => {
        const editedIds = Object.keys(editedRows).map(Number);
        if (editedIds.length === 0) {
            setIsEdit(false);
            return;
        }
        editedIds.forEach(hocSinhId => {
            const row = editedRows[hocSinhId];
            const originalData = dataSource.find((x: any) => x.ts_hocsinh_id === hocSinhId);
            const existingData = ketQuasThangHienTai.find((x: any) => x.ts_hocsinh_id === hocSinhId);
            const chieuCao = row.chieu_cao !== undefined ? (parseFloat(row.chieu_cao) || 0) : (originalData?.chieu_cao || 0);
            const canNang = row.can_nang !== undefined ? (parseFloat(row.can_nang) || 0) : (originalData?.can_nang || 0);
            const ngayDoValue = row.ngay_do ? moment(row.ngay_do).format('YYYY-MM-DDTHH:mm:ss') : moment(ngay_do).format('YYYY-MM-DDTHH:mm:ss');
            dispatch(actions.chieuCaoCanNang.SAVE_START({
                id: existingData?.id ?? 0,
                ts_hocsinh_id: hocSinhId,
                nam_hoc: filter.nam_hoc,
                nam: new Date().getFullYear(),
                thang: filter.thang,
                chieu_cao: chieuCao,
                can_nang: canNang,
                ngay_do: ngayDoValue as any
            }));
        });
        setEditedRows({});
        setIsEdit(false);
    }, [editedRows, dataSource, ketQuasThangHienTai, filter, ngay_do, dispatch]);

    const handleDelete = useCallback((id: number) => {
        if (window.confirm('Bạn có muốn xóa dữ liệu này không?')) {
            dispatch(actions.chieuCaoCanNang.DELETE_START(id));
        }
    }, [dispatch]);

    const handleExportExcel = useCallback(() => {
        const grade = getGradeFromName(lopSelected.ten_lop);
        const exportData = dataSource.map((item: any, idx: number) => ({
            'STT': idx + 1,
            'Mã học sinh': item.ma_hs ?? '',
            'Họ tên': item.ho_ten ?? '',
            'Tháng': filter.thang,
            'Chiều cao': item.chieu_cao ?? '',
            'Cân nặng': item.can_nang ?? '',
            'BMI': item.bmi != null ? Math.trunc(item.bmi * 10) / 10 : '',
            'Ngày đo': item.ngay_do ? moment(item.ngay_do).format('DD/MM/YYYY HH:mm') : '',
            'Thể trạng': getBMILabel(item.bmi, grade).label,
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        ws['!cols'] = [
            { wch: 5 }, { wch: 15 }, { wch: 25 }, { wch: 8 },
            { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 20 }, { wch: 16 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Chiều cao cân nặng');
        XLSX.writeFile(wb, `Chieu_cao_can_nang_T${filter.thang}_${lopSelected.ten_lop}_${moment().format('DD-MM-YYYY')}.xlsx`);
    }, [dataSource, filter.thang, lopSelected.ten_lop]);

    const viewChart = async (hocSinhId: number) => {
        const hs = dataSource.find((x: any) => x.ts_hocsinh_id === hocSinhId || x.id === hocSinhId);
        setSelectedHocSinh(hs);
        const res = await chieuCaoCanNangApi.selectByHocSinh(filter.nam_hoc, hocSinhId);
        if (res.is_success) {
            setHocSinhChartData(res.data);
            setShowChartModal(true);
            setChartTabIndex(0);
        }
    };

    return (
        <Box sx={{ height: "calc(100vh - 100px)", display: "flex" }}>
            {/* LEFT: Lop TreeView */}
            <Box sx={{ pt: 3, width: "260px", flexShrink: 0, borderRight: '1px solid #d0d7de', overflow: 'auto' }}>
                {lopStatus == ePageBaseStatus.is_loading ? (
                    <PlaceHolder line_number={10} />
                ) : (
                    <LopHanhChinhTreeView
                        dataSource={treeViewItems}
                        nam_hoc={nam_hoc}
                        dm_truong_id={dm_truong_selected_id}
                        selectedValue={lopSelected.dm_lop}
                        onSelectionChanged={handleLopSelected}
                    />
                )}
            </Box>

            {/* RIGHT: Content */}
            <Box sx={{ flex: 1, overflow: "auto", display: 'flex', flexDirection: 'column', p: 3 }}>
                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {lopSelected.dm_lop.dm_lop_id > 0 ? (
                        <DataTable
                            title={"Chiều cao - Cân nặng"}
                            actionComponent={
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    {!isEdit && <ActionMenu>
                                        <ActionMenu.Button size="small">
                                            {thangs.find((t: any) => t.id === filter.thang)?.thang ?? 'Chọn tháng'}
                                        </ActionMenu.Button>
                                        <ActionMenu.Overlay width="auto">
                                            <ActionList selectionVariant="single">
                                                {thangs.map((t: any) => (
                                                    <ActionList.Item
                                                        key={t.id}
                                                        selected={t.id === filter.thang}
                                                        onSelect={() => dispatch(actions.chieuCaoCanNang.CHANGE_FILTER({ ...filter, thang: t.id }))}
                                                    >
                                                        {t.thang}
                                                    </ActionList.Item>
                                                ))}
                                            </ActionList>
                                        </ActionMenu.Overlay>
                                    </ActionMenu>}
                                    {isEdit && (
                                        <TextInput
                                            type="datetime-local"
                                            size="small"
                                            value={moment(ngay_do).format('YYYY-MM-DDTHH:mm')}
                                            onChange={(e) => setNgayDo(new Date(e.target.value))}
                                        />
                                    )}
                                    {isEdit && (
                                        <Button
                                            leadingVisual={CheckIcon}
                                            onClick={handleSaveAll}
                                            size="small"
                                            variant="primary"
                                        >
                                            Lưu
                                        </Button>
                                    )}
                                    <Button
                                        leadingVisual={isEdit ? XIcon : PencilIcon}
                                        onClick={() => { setIsEdit(!isEdit); setEditedRows({}); }}
                                        size="small"
                                    >
                                        {isEdit ? translate('Hủy') : translate('Chỉnh sửa')}
                                    </Button>
                                    <Button leadingVisual={UploadIcon} onClick={() => setShowImportModal(true)} size="small">
                                        Import
                                    </Button>
                                    <Button leadingVisual={DownloadIcon} onClick={handleExportExcel} size="small">
                                        Export
                                    </Button>
                                </Box>
                            }
                            data={dataSource}
                            height={`${window.innerHeight - 180}px`}
                            columns={[
                                {
                                    caption: 'Mã học sinh',
                                    dataField: 'ma_hs',
                                    width: '120px',
                                    align: 'center',
                                },
                                {
                                    caption: 'Họ tên',
                                    dataField: 'ho_ten',
                                    width: '220px',
                                    cellRender: (data: any) => (
                                        <HocSinhShortInfo data={data} />
                                    )
                                },
                                {
                                    caption: 'Chiều cao (cm)',
                                    dataField: 'chieu_cao',
                                    width: '130px',
                                    align: 'center',
                                    cellRender: (data: any) => isEdit ? (
                                        <EditableCell
                                            value={editedRows[data.ts_hocsinh_id]?.chieu_cao ?? data.chieu_cao}
                                            onChange={(val) => handleFieldChange(data.ts_hocsinh_id, 'chieu_cao', val)}
                                        />
                                    ) : (
                                        <span>{data.chieu_cao ?? '-'}</span>
                                    )
                                },
                                {
                                    caption: 'Cân nặng (kg)',
                                    dataField: 'can_nang',
                                    width: '130px',
                                    align: 'center',
                                    cellRender: (data: any) => isEdit ? (
                                        <EditableCell
                                            value={editedRows[data.ts_hocsinh_id]?.can_nang ?? data.can_nang}
                                            onChange={(val) => handleFieldChange(data.ts_hocsinh_id, 'can_nang', val)}
                                        />
                                    ) : (
                                        <span>{data.can_nang ?? '-'}</span>
                                    )
                                },
                                {
                                    caption: 'BMI',
                                    dataField: 'bmi',
                                    width: '80px',
                                    align: 'center',
                                    cellRender: (data: any) => (
                                        <span>{data.bmi != null ? Math.trunc(data.bmi * 10) / 10 : '-'}</span>
                                    )
                                },
                                {
                                    caption: 'Thể trạng',
                                    dataField: 'the_trang',
                                    width: '130px',
                                    align: 'center',
                                    cellRender: (data: any) => {
                                        const grade = getGradeFromName(lopSelected.ten_lop);
                                        const { label, color } = getBMILabel(data.bmi, grade);
                                        return (
                                            <span style={{ color, fontWeight: 600 }}>{label}</span>
                                        );
                                    }
                                },
                                {
                                    caption: 'Ngày đo',
                                    dataField: 'ngay_do',
                                    width: '160px',
                                    align: 'center',
                                    cellRender: (data: any) => isEdit ? (
                                        <EditableDateTimeCell
                                            value={editedRows[data.ts_hocsinh_id]?.ngay_do ?? data.ngay_do}
                                            onChange={(val) => handleFieldChange(data.ts_hocsinh_id, 'ngay_do', val)}
                                        />
                                    ) : (
                                        <span>{data.ngay_do ? moment(data.ngay_do).format('DD/MM/YYYY HH:mm') : '-'}</span>
                                    )
                                },
                                {
                                    caption: '',
                                    dataField: 'action',
                                    width: '80px',
                                    align: 'center',
                                    cellRender: (data: any) => (
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                            <Tooltip text="Xem biểu đồ" direction="w">
                                                <IconButton icon={EyeIcon} aria-label="view-chart" size="small" variant="invisible"
                                                    onClick={() => viewChart(data.ts_hocsinh_id)} />
                                            </Tooltip>
                                            {isEdit && data.id > 0 && (
                                                <Tooltip text="Xóa" direction="w">
                                                    <IconButton icon={XIcon} aria-label="delete" size="small" variant="danger"
                                                        onClick={() => handleDelete(data.id)} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )
                                },
                            ]}
                        />
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 5, color: 'fg.muted' }}>
                            Vui lòng chọn lớp bên trái
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Import modal */}
            {showImportModal && (
                <Modal
                    isOpen
                    onClose={() => setShowImportModal(false)}
                    sx={{ width: '900px', maxHeight: '95vh' }}
                    title="Import Chiều cao - Cân nặng"
                >
                    <Box sx={{ p: 0 }}>
                        <ChieuCaoCanNangImport
                            onClose={() => setShowImportModal(false)}
                            dm_truong_id={lopSelected.dm_lop.dm_truong_id || dm_truong_selected_id}
                        />
                    </Box>
                </Modal>
            )}

            {/* Chart modal - matching source project DetailTemplate */}
            {showChartModal && (
                <Modal
                    isOpen
                    onClose={() => setShowChartModal(false)}
                    sx={{ width: '700px', maxHeight: '95vh' }}
                    title="Biểu đồ chiều cao - cân nặng"
                >
                    <Box sx={{ p: 0, maxHeight: 'calc(100vh - 160px)', overflow: 'auto' }}>
                        {/* Student info */}
                        <Box sx={{ display: 'flex', mt: 0 }}>
                            <Box sx={{ display: 'flex', flex: 1 }}>
                                <Box sx={{ mr: 2, width: 40, height: 40, borderRadius: '50%', backgroundColor: '#2da44e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600, fontSize: 16 }}>
                                    {(selectedHocSinh?.ho_ten ?? '?').charAt(0).toUpperCase()}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ fontWeight: 600, fontSize: 24 }}>
                                        {selectedHocSinh?.ho_ten ?? ''}
                                    </Box>
                                    <Box sx={{ color: 'fg.muted' }}>
                                        Giới tính: {selectedHocSinh?.gioi_tinh ?? '-'} - {selectedHocSinh?.ngay_sinh ? tinhTuoi(moment(selectedHocSinh.ngay_sinh).toDate()) : ''}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {/* Summary cards */}
                        <Box sx={{ display: 'flex', mt: 2, mb: 2, gap: 2 }}>
                            {[{ name: 'Cân nặng', value: selectedHocSinh?.can_nang, unit: 'kg' },
                            { name: 'Chiều cao', value: selectedHocSinh?.chieu_cao, unit: 'cm' },
                            { name: 'BMI', value: selectedHocSinh?.bmi, unit: 'kg/m2' }].map((x, idx) => (
                                <Box key={idx} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#f6f8fa', p: 2, borderRadius: '10px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', pb: 1 }}>
                                        <Box sx={{ fontSize: 24, fontWeight: 600 }}>{x.value ?? '-'}</Box>
                                        <Box sx={{ ml: 2, color: 'fg.muted' }}>{x.unit}</Box>
                                    </Box>
                                    <Box sx={{ color: 'fg.muted', fontWeight: 600 }}>{x.name}</Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Chart tabs */}
                        <Box sx={{ pb: 2 }}>
                            <SegmentedControl onChange={setChartTabIndex} aria-label="Chart">
                                <SegmentedControl.Button selected={chartTabIndex === 0}>Biểu đồ Cân nặng</SegmentedControl.Button>
                                <SegmentedControl.Button selected={chartTabIndex === 1}>Biểu đồ Chiều cao</SegmentedControl.Button>
                                <SegmentedControl.Button selected={chartTabIndex === 2}>Biểu đồ BMI</SegmentedControl.Button>
                            </SegmentedControl>
                        </Box>

                        {/* Chart */}
                        <Box sx={{ backgroundColor: '#fff', p: 2, mb: 2, borderRadius: '10px' }}>
                            {chartTabIndex === 0 && <CanNangChart dataSource={hocSinhChartData.filter((x: any) => x.so_thang > 0).sort((a: any, b: any) => parseInt(a.so_thang, 10) - parseInt(b.so_thang, 10))} />}
                            {chartTabIndex === 1 && <ChieuCaoChart dataSource={hocSinhChartData.filter((x: any) => x.so_thang > 0)} />}
                            {chartTabIndex === 2 && <BMIChartChart grade={getGradeFromName(lopSelected.ten_lop)} dataSource={hocSinhChartData.filter((x: any) => x.so_thang > 0).sort((a: any, b: any) => parseInt(a.so_thang, 10) - parseInt(b.so_thang, 10))} />}
                        </Box>

                        {/* Data table */}
                        <Box sx={{ backgroundColor: '#fff', p: 2, borderRadius: '10px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f6f8fa' }}>
                                        <th style={thStyle}>STT</th>
                                        <th style={thStyle}>Ngày đo</th>
                                        <th style={thStyle}>Tháng tuổi</th>
                                        <th style={thStyle}>Cân nặng (kg)</th>
                                        <th style={thStyle}>Chiều cao (cm)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {hocSinhChartData.filter((x: any) => x.so_thang > 0).sort((a: any, b: any) => parseInt(b.so_thang, 10) - parseInt(a.so_thang, 10)).map((item: any, idx: number) => (
                                        <tr key={idx}>
                                            <td style={tdStyle}>{idx + 1}</td>
                                            <td style={tdStyle}>{item.ngay_do ? moment(item.ngay_do).format('DD/MM/YYYY HH:mm') : '-'}</td>
                                            <td style={tdStyle}>{item.so_thang ? formatThangTuoi(item.so_thang) : '-'}</td>
                                            <td style={tdStyle}>{item.can_nang ?? '-'}</td>
                                            <td style={tdStyle}>{item.chieu_cao ?? '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    </Box>
                </Modal>
            )}
        </Box>
    );
};

// Style helpers (for modal data table)
const thStyle: React.CSSProperties = {
    padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #d0d7de', fontWeight: 600, fontSize: 13
};
const tdStyle: React.CSSProperties = {
    padding: '6px 12px', borderBottom: '1px solid #d0d7de', fontSize: 13
};

// Simple editable cell - no save logic, just reports changes
const EditableCell = ({ value, onChange }: { value: any; onChange: (val: string) => void }) => {
    return (
        <TextInput size="small" sx={{ width: 80 }}
            defaultValue={value?.toString() ?? ''}
            onChange={(e) => onChange(e.target.value)} />
    );
};

// Simple editable datetime cell - no save logic, just reports changes
const EditableDateTimeCell = ({ value, onChange }: { value: any; onChange: (val: string) => void }) => {
    return (
        <TextInput type="datetime-local" size="small" sx={{ width: 200 }}
            defaultValue={value ? moment(value).format('YYYY-MM-DDTHH:mm') : ''}
            onChange={(e) => onChange(e.target.value)} />
    );
};

export default ChieuCaoCanNangPage;
